/**
 * SearchModal Component
 *
 * Global search modal triggered by Cmd+K / Ctrl+K.
 * Searches fixtures, teams, and referees across ALL leagues.
 * Clicking a result navigates to that fixture.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigation } from '../context/NavigationContext'
import { apiGet } from '../api'

// ============================================
// TYPES
// ============================================

// Raw fixture data from search API
interface SearchFixture {
  id: number
  api_fixture_id: number
  home_team: { id: number; name: string; crest_url?: string | null }
  away_team: { id: number; name: string; crest_url?: string | null }
  kickoff_utc: string | null
  status: string
  status_detail?: string | null
  home_score?: number | null
  away_score?: number | null
  round?: string | null
  venue?: string | null
  league: { id: number; name: string; api_league_id: number } | null
}

interface SearchResult {
  id: string | number
  type: 'fixture' | 'team' | 'referee'
  title: string
  subtitle?: string
  meta?: string
  /** Full fixture data for navigation */
  fixture?: SearchFixture
  /** League API ID for switching leagues when navigating */
  leagueApiId?: number
  /** Referee ID for referee navigation */
  refereeId?: number
  /** Leagues this referee officiates in */
  refereeLeagues?: Array<{ id: number; name: string; api_league_id: number }>
}

// ============================================
// COMPONENT
// ============================================

export function SearchModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>(() => {
    const stored = localStorage.getItem('statscout_recent_searches')
    return stored ? JSON.parse(stored) : []
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const { navigateToFixture, navigateToReferee } = useNavigation()

  // ---- Keyboard shortcut to open ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // ---- Focus input when opened ----
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
    } else {
      setQuery('')
      setResults([])
      setSelectedIndex(0)
    }
  }, [isOpen])

  // ---- Debounced search ----
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const data = await apiGet<{
          fixtures: SearchFixture[]
          teams: Array<{
            id: number
            name: string
            short_name: string | null
            crest_url: string | null
            league: { id: number; name: string; api_league_id: number } | null
            next_fixture_id: number | null
          }>
          referees: Array<{
            id: number
            name: string
            country: string | null
          }>
        }>(`/search?q=${encodeURIComponent(query)}`)

        const searchResults: SearchResult[] = []

        // Map fixtures first (most relevant for betting)
        data.fixtures?.forEach((f) => {
          searchResults.push({
            id: f.id,
            type: 'fixture',
            title: `${f.home_team.name} vs ${f.away_team.name}`,
            subtitle: f.kickoff_utc
              ? new Date(f.kickoff_utc).toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : undefined,
            meta: f.league?.name,
            fixture: f,
            leagueApiId: f.league?.api_league_id,
          })
        })

        // Map teams (with their next fixture - but we don't have fixture data for teams)
        data.teams?.forEach((t) => {
          searchResults.push({
            id: t.id,
            type: 'team',
            title: t.name,
            subtitle: t.next_fixture_id ? 'Next match available' : 'No upcoming matches',
            meta: t.league?.name,
            leagueApiId: t.league?.api_league_id,
          })
        })

        // Map referees (navigate to referees page with league)
        data.referees?.forEach((r: { id: number; name: string; country?: string | null; leagues?: Array<{ id: number; name: string; api_league_id: number }> }) => {
          // Get the first league as the default navigation target
          const primaryLeague = r.leagues?.[0]
          const leagueNames = r.leagues?.map((l) => l.name).join(', ') || 'No leagues'
          searchResults.push({
            id: r.id,
            type: 'referee',
            title: r.name,
            subtitle: r.country ?? undefined,
            meta: leagueNames,
            refereeId: r.id,
            refereeLeagues: r.leagues,
            leagueApiId: primaryLeague?.api_league_id,
          })
        })

        setResults(searchResults)
      } catch (err) {
        console.error('Search failed:', err)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [query])

  // ---- Keyboard navigation ----
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      const displayResults = query.length >= 2 ? results : recentSearches

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, displayResults.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (displayResults[selectedIndex]) {
            handleSelect(displayResults[selectedIndex])
          }
          break
      }
    },
    [results, recentSearches, selectedIndex, query]
  )

  // ---- Handle selection ----
  const handleSelect = (result: SearchResult) => {
    // Add to recent searches - keep fixture data for navigation to work
    const newRecent = [result, ...recentSearches.filter((r) => r.id !== result.id)].slice(0, 5)
    setRecentSearches(newRecent)
    localStorage.setItem('statscout_recent_searches', JSON.stringify(newRecent))

    // Navigate based on result type
    if (result.type === 'fixture' && result.fixture) {
      // Navigate with full fixture data
      navigateToFixture(
        {
          id: result.fixture.id,
          api_fixture_id: result.fixture.api_fixture_id,
          kickoff_utc: result.fixture.kickoff_utc || new Date().toISOString(),
          status: result.fixture.status,
          status_detail: result.fixture.status_detail,
          home_score: result.fixture.home_score,
          away_score: result.fixture.away_score,
          round: result.fixture.round,
          venue: result.fixture.venue,
          home_team: result.fixture.home_team,
          away_team: result.fixture.away_team,
        },
        result.leagueApiId
      )
    } else if (result.type === 'team') {
      // Teams don't have fixture data - just show a message
      // TODO: Could fetch the team's next fixture and navigate to it
      console.log('Team selected, but no fixture data available')
    } else if (result.type === 'referee' && result.refereeId) {
      // Navigate to referees page with this referee selected
      navigateToReferee(result.refereeId, result.leagueApiId)
    }

    setIsOpen(false)
  }

  // ---- Clear recent searches ----
  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('statscout_recent_searches')
  }

  // ---- Get icon for result type ----
  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'fixture':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )
      case 'team':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        )
      case 'referee':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M9 21V9" />
          </svg>
        )
    }
  }

  if (!isOpen) return null

  const displayResults = query.length >= 2 ? results : recentSearches
  const showRecentHeader = query.length < 2 && recentSearches.length > 0

  return (
    <>
      {/* Backdrop */}
      <div className="search-modal-backdrop" onClick={() => setIsOpen(false)} />

      {/* Modal */}
      <div className="search-modal" role="dialog" aria-modal="true" aria-label="Search">
        {/* Input */}
        <div className="search-input-wrapper">
          <svg
            className="search-input-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search fixtures, teams..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
            }}
            onKeyDown={handleKeyNavigation}
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="search-results"
          />
          <div className="search-hints">
            <kbd>ESC</kbd>
          </div>
        </div>

        {/* Results */}
        <div
          id="search-results"
          className="search-results"
          ref={listRef}
          role="listbox"
          aria-label="Search results"
        >
          {isSearching && (
            <div className="search-loading">
              <div className="search-loading-spinner" />
              Searching...
            </div>
          )}

          {!isSearching && showRecentHeader && (
            <div className="search-results-header">
              <span>Recent Searches</span>
              <button onClick={clearRecentSearches} className="search-clear-recent">
                Clear
              </button>
            </div>
          )}

          {!isSearching && query.length >= 2 && results.length === 0 && (
            <div className="search-empty">
              <p>No results found for "{query}"</p>
            </div>
          )}

          {!isSearching &&
            displayResults.map((result, index) => {
              // Only teams are disabled (referees now navigate to referees page)
              const isDisabled = result.type === 'team'
              return (
                <div
                  key={`${result.type}-${result.id}`}
                  className={`search-result-item ${index === selectedIndex ? 'selected' : ''} ${
                    isDisabled ? 'disabled' : ''
                  }`}
                  onClick={() => {
                    if (isDisabled) return
                    handleSelect(result)
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="option"
                  aria-selected={index === selectedIndex}
                  aria-disabled={isDisabled}
                >
                  <span className="search-result-icon">{getTypeIcon(result.type)}</span>
                  <div className="search-result-content">
                    <span className="search-result-title">{result.title}</span>
                    {result.subtitle && (
                      <span className="search-result-subtitle">{result.subtitle}</span>
                    )}
                  </div>
                  {result.meta && <span className="search-result-meta">{result.meta}</span>}
                  <span className="search-result-type">{result.type}</span>
                </div>
              )
            })}
        </div>

        {/* Footer */}
        <div className="search-footer">
          <div className="search-footer-hint">
            <kbd>↑</kbd> <kbd>↓</kbd> to navigate
          </div>
          <div className="search-footer-hint">
            <kbd>↵</kbd> to select
          </div>
          <div className="search-footer-hint">
            <kbd>esc</kbd> to close
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * SearchTrigger Component
 *
 * Button to open the search modal, shows keyboard shortcut hint.
 */
interface SearchTriggerProps {
  onClick?: () => void
  className?: string
}

export function SearchTrigger({ onClick, className = '' }: SearchTriggerProps) {
  const [isMac, setIsMac] = useState(false)

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0)
  }, [])

  const handleClick = () => {
    // Trigger the global keyboard shortcut
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      metaKey: isMac,
      ctrlKey: !isMac,
      bubbles: true,
    })
    document.dispatchEvent(event)
    onClick?.()
  }

  return (
    <button
      className={`search-trigger ${className}`}
      onClick={handleClick}
      aria-label="Search (Cmd+K)"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <span className="search-trigger-text">Search...</span>
      <span className="search-trigger-shortcut">
        <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd>
        <kbd>K</kbd>
      </span>
    </button>
  )
}

export default SearchModal
